/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useConferenceDelete.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing conference deletion functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';

interface UseConferenceDeleteOptions {
  conferenceId?: string;
  tenantSlug?: string;
  userId?: string | number;
  onSuccess?: () => void;
}

export function useConferenceDelete({
  conferenceId,
  tenantSlug,
  userId,
  onSuccess,
}: UseConferenceDeleteOptions) {
  const { showSuccess, showError } = useNotifications();
  const { t } = useI18n();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = useCallback(() => {
    if (!conferenceId) return;
    setShowDeleteModal(true);
  }, [conferenceId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!conferenceId) return;

    setIsDeleting(true);
    try {
      const response = await fetch(buildApiUrl(`/api/telephony/conferences/${conferenceId}`), {
        method: 'DELETE',
        headers: getApiHeaders(tenantSlug, userId),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete conference');
      }

      showSuccess(t('conferences:messages.deleteSuccess', 'Conference deleted successfully'));
      setShowDeleteModal(false);
      onSuccess?.();
    } catch (error) {
      showError(error.message || t('conferences:messages.deleteError', 'Failed to delete conference'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [conferenceId, tenantSlug, userId, onSuccess, showSuccess, showError, t]);

  return {
    showDeleteModal,
    setShowDeleteModal,
    isDeleting,
    handleDeleteClick,
    handleDeleteConfirm,
  };
}

